export interface DisplayMeta {
  label: string;
  color: string;
  dot: string;
}

const STATUS_FAMILIES = {
  active: { color: 'bg-status-active-background text-status-active-text', dot: 'bg-status-active-dot' },
  progress: { color: 'bg-status-progress-background text-status-progress-text', dot: 'bg-status-progress-dot' },
  waiting: { color: 'bg-status-waiting-background text-status-waiting-text', dot: 'bg-status-waiting-dot' },
  product: { color: 'bg-status-product-background text-status-product-text', dot: 'bg-status-product-dot' },
  attention: { color: 'bg-status-attention-background text-status-attention-text', dot: 'bg-status-attention-dot' },
  done: { color: 'bg-status-done-background text-status-done-text', dot: 'bg-status-done-dot' },
  terminal: { color: 'bg-status-terminal-background text-status-terminal-text', dot: 'bg-status-terminal-dot' },
} as const;

export const STATUS_FALLBACK: DisplayMeta = {
  label: 'Unknown', color: 'bg-surface-subtle text-ink-muted', dot: 'bg-ink-subtle',
};

export const STATUS_MAP: Record<string, DisplayMeta> = {
  open: { label: 'Open', ...STATUS_FAMILIES.active },
  to_be_prioritised: { label: 'To Be Prioritised', ...STATUS_FAMILIES.active },
  work_in_progress: { label: 'Work in Progress', ...STATUS_FAMILIES.progress },
  assigned_to_ai_agent: { label: 'Assigned to AI Agent', ...STATUS_FAMILIES.progress },
  pending: { label: 'Pending', ...STATUS_FAMILIES.waiting },
  awaiting_user_reply: { label: 'Awaiting User Reply', ...STATUS_FAMILIES.waiting },
  awaiting_resolution_confirmation: { label: 'Awaiting Resolution Confirmation', ...STATUS_FAMILIES.waiting },
  on_hold: { label: 'On Hold', ...STATUS_FAMILIES.waiting },
  pending_production_release: { label: 'Pending Production Release', ...STATUS_FAMILIES.waiting },
  product_cr: { label: 'Product CR', ...STATUS_FAMILIES.product },
  reopened: { label: 'Reopened', ...STATUS_FAMILIES.attention },
  issue_fixed: { label: 'Issue Fixed', ...STATUS_FAMILIES.done },
  resolved: { label: 'Resolved', ...STATUS_FAMILIES.done },
  closed: { label: 'Closed', ...STATUS_FAMILIES.terminal },
  cto_approval_pending: { label: 'CTO Approval Pending', ...STATUS_FAMILIES.waiting },
  po_approval_pending: { label: 'PO Approval Pending', ...STATUS_FAMILIES.waiting },
  rm_approval_pending: { label: 'RM Approval Pending', ...STATUS_FAMILIES.waiting },
  rework_required: { label: 'Rework Required', ...STATUS_FAMILIES.attention },
  approved: { label: 'Approved', ...STATUS_FAMILIES.done },
  rejected: { label: 'Rejected', ...STATUS_FAMILIES.terminal },
};

export function resolveStatus(value: string | null | undefined, label?: string): DisplayMeta {
  const status = STATUS_MAP[value ?? ''] ?? STATUS_FALLBACK;
  return { ...status, label: label ?? status.label };
}

export const PRIORITY_MAP: Record<string, DisplayMeta> = {
  low: { label: 'Low', color: 'bg-surface-subtle text-ink-muted', dot: 'bg-ink-subtle' },
  medium: { label: 'Medium', color: 'bg-status-product-background text-status-product-text', dot: 'bg-status-product-dot' },
  high: { label: 'High', color: 'bg-warning-50 text-warning-700', dot: 'bg-warning-500' },
  urgent: { label: 'Urgent', color: 'bg-danger-50 text-danger-600', dot: 'bg-danger-500' },
};

export function resolvePriority(value: string | null | undefined, label?: string): DisplayMeta {
  const priority = PRIORITY_MAP[value ?? ''] ?? STATUS_FALLBACK;
  return { ...priority, label: label ?? priority.label };
}

export const SOURCE_MAP: Record<string, { label: string }> = {
  email: { label: 'Email' }, portal: { label: 'Portal' }, phone: { label: 'Phone' }, chat: { label: 'Chat' },
  whatsapp: { label: 'WhatsApp' }, web_chat: { label: 'Web Chat' }, web_form: { label: 'Web Form' }, forum: { label: 'Forum' },
  facebook: { label: 'Facebook' }, mobihelp: { label: 'MobiHelp' }, feedback_widget: { label: 'Feedback Widget' },
  outbound_email: { label: 'Outbound Email' }, ecommerce: { label: 'Ecommerce' }, bot: { label: 'Bot' },
  instagram_message: { label: 'Instagram Message' }, instagram_comment: { label: 'Instagram Comment' },
  facebook_message: { label: 'Facebook Message' }, facebook_comment: { label: 'Facebook Comment' },
  mobile_chat_sdk: { label: 'Mobile Chat SDK' }, sms: { label: 'SMS' }, api: { label: 'API' },
};

export const ROLE_MAP = {
  super_admin: { label: 'Super Admin', color: 'bg-status-product-background text-status-product-text' },
  administrator: { label: 'Administrator', color: 'bg-status-progress-background text-status-progress-text' },
  supervisor: { label: 'Supervisor', color: 'bg-info-50 text-sky-600' },
  agent: { label: 'Agent', color: 'bg-status-done-background text-status-done-text' },
  collaborator: { label: 'Collaborator', color: 'bg-status-active-background text-status-active-text' },
  customer: { label: 'Customer', color: 'bg-surface-subtle text-ink-muted' },
} as const;

type OptionalTranslator = (key: string, fallback: string) => string;

/**
 * Localise a status/priority/source label. Admin-configured custom labels (an
 * explicit label that differs from the built-in English default) are kept as
 * typed; built-in values are translated through the active catalog.
 */
export function localizeLabel(
  kind: 'status' | 'priority' | 'source',
  value: string | null | undefined,
  defaultLabel: string,
  tOr: OptionalTranslator,
  explicitLabel?: string,
): string {
  if (explicitLabel !== undefined && explicitLabel !== defaultLabel) return explicitLabel;
  return value ? tOr(`${kind}.${value}`, defaultLabel) : defaultLabel;
}
